import { z } from "zod";

/**
 * What the API reports about itself at `GET /internal/metrics` (P11-T13,
 * infrastructure.md, Monitoring). JSON, read on the machine itself and, from
 * P11-T14, by the admin dashboard through an admin procedure.
 */

const count = z.number().int().nonnegative();
const ms = z.number().int().nonnegative();

/** Request durations: the upper bound (ms) of the histogram bucket each percentile falls in. */
export const latencySchema = z.object({ p50: ms, p95: ms, p99: ms, max: ms });

export const requestStatsSchema = z.object({
	count,
	/** Answers with status 500 and above. */
	errors: count,
	latencyMs: latencySchema,
});

export const socketStatsSchema = z.object({
	sockets: count,
	staff: count,
	guest: count,
	public: count,
});
export type SocketStats = z.infer<typeof socketStatsSchema>;

/** Filled by the printing module (Phase 5) through `metrics.setPrintJobSource`. */
export const printJobStatsSchema = z.object({
	queued: count,
	/** Seconds the oldest queued job has waited; null without queued jobs. */
	oldestQueuedSeconds: z.number().int().nonnegative().nullable(),
	failedLast24h: count,
});
export type PrintJobStats = z.infer<typeof printJobStatsSchema>;

export const systemMetricsSchema = z.object({
	at: z.iso.datetime(),
	startedAt: z.iso.datetime(),
	process: z.object({ rssBytes: count, heapUsedBytes: count }),
	requests: z.object({
		/** Since the process started, by status class. */
		total: count,
		byStatus: z.object({ "2xx": count, "3xx": count, "4xx": count, "5xx": count }),
		/** The last five whole minutes and the current one. */
		lastFiveMinutes: requestStatsSchema,
		/** Since the process started, busiest first, at most 25. */
		routes: z.array(requestStatsSchema.extend({ route: z.string() })),
	}),
	/** Null when this process has no WebSocket gateway. */
	sockets: socketStatsSchema.nullable(),
	/** pg-boss queues as its monitor last counted them; empty before the worker has started. */
	queues: z.array(
		z.object({
			name: z.string(),
			queued: count,
			ready: count,
			active: count,
			deferred: count,
			failed: count,
			countedAt: z.iso.datetime().nullable(),
		}),
	),
	outbox: z.object({
		pending: count,
		/** Age of the oldest undispatched event: how far the dispatcher is behind. */
		lagSeconds: z.number().int().nonnegative().nullable(),
		/** Undispatched events that have failed at least once. */
		retrying: count,
	}),
	/** Null until printing (Phase 5) registers its source. */
	printJobs: printJobStatsSchema.nullable(),
});
export type SystemMetrics = z.infer<typeof systemMetricsSchema>;
